import axios from './axios';

/**
 * Fetch a CSV export and hand it to the browser as a download.
 *
 * Extracted because the same eight lines were about to be copy-pasted onto five pages,
 * and two details in them are easy to get wrong in a way that only shows up later:
 *
 *   1. `URL.revokeObjectURL` must be called. Without it every export leaks a blob for
 *      the lifetime of the tab — an admin working through a morning of reports
 *      accumulates them, and admin sessions are long-lived.
 *   2. The filename should come from the SERVER's Content-Disposition. The backend
 *      already builds a slugged, date-stamped, header-safe name; rebuilding it in the
 *      client means two sources of truth that drift, and a client-built name can't know
 *      about server-side clamping (e.g. the window actually used after validation).
 *
 * @returns {Promise<{ok: boolean, filename?: string, error?: string}>}
 *   Resolves rather than throwing, so a failed export can be reported inline on the page
 *   instead of needing a try/catch at every call site.
 */

/** Pull the filename out of a Content-Disposition header, if the server sent one. */
const filenameFromHeaders = (headers) => {
  const raw = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
  // RFC 5987 form first (filename*=UTF-8''…), then the plain quoted form.
  const star = /filename\*=UTF-8''([^;]+)/i.exec(raw);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(raw);
  return plain?.[1]?.trim() || '';
};

export const downloadCsv = async (url, params = {}, fallbackName = 'export.csv') => {
  let objectUrl;
  try {
    const res = await axios.get(url, {
      params,
      responseType: 'blob',
      // The page reports its own inline error; a toast on top of that is noise.
      skipNotify: true,
    });

    // An error response can still arrive as a blob (responseType applies regardless of
    // status), so an unchecked success path would "download" a JSON error body as a .csv.
    const type = res.data?.type || '';
    if (type.includes('application/json')) {
      const text = await res.data.text();
      let msg = 'Export failed.';
      try { msg = JSON.parse(text)?.msg || msg; } catch { /* keep the default */ }
      return { ok: false, error: msg };
    }

    const filename = filenameFromHeaders(res.headers) || fallbackName;

    objectUrl = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    return { ok: true, filename };
  } catch (err) {
    return { ok: false, error: err?.response?.data?.msg || 'Export failed.' };
  } finally {
    // Always revoked, including on the JSON-error path above, which returns early.
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
};

export default downloadCsv;
